import { EmptyGuid } from "../emptyGuid";
import { Bootgrid } from "./bootgrid";
import { BootgridManager } from "./bootgridManager";
import { PageState } from "./bootgridTypes";
import initRecordPageViewManager from "./init-record-page-view-manager";
import { PageStateConstructor } from "./PageStateConstructor";
import postPageState from "./post-page-state";
import { gridViewApi } from "./ViewManager/ViewApi/GridViewApi";
import { BootgridState } from "./ViewManager/viewManagerTypes";
import { initDragAndDropTarget } from "../overrides";

export default async function initRecordPageBootgrids(bootgridElements: JQuery<HTMLElement>, config: Config): Promise<void> {
    if (bootgridElements.length === 0) return;

    const pageState = await getPageState();
    const bootgrids = await createBootgrids(bootgridElements, config, pageState);

    if (bootgrids.length === 0) return;

    initRecordPageViewManager(bootgrids, pageState?.selectedPageView ?? "default");

    const bootgridManager = new BootgridManager(bootgrids);
    initializeBootgrids(bootgrids, bootgridManager, pageState);

    setupEventListeners();
    initDragAndDropTarget();
}

function initializeBootgrids(bootgrids: Bootgrid[], bootgridManager: BootgridManager, pageState: PageState): void {
    bootgrids.forEach((bootgrid) => {
        const selectedViewId = pageState?.selectedGridViews?.[bootgrid.menuItemName] ?? "default";

        bootgrid.bootgridManager = bootgridManager;
        bootgrid.init(undefined, pageState, selectedViewId);
    });
}

async function createBootgrids(bootgridElements: JQuery<HTMLElement>, config: Config, pageState: PageState): Promise<Bootgrid[]> {
    const bootgrids: Bootgrid[] = [];

    for (const element of bootgridElements.get()) {
        if ($(element).attr("bootgrid-initialized") === "true") continue;
        $(element).attr("bootgrid-initialized", "true");

        const bootgrid = new Bootgrid(config, $(element));
        const bootgridState = await getBootgridState(pageState, bootgrid.menuItemName);

        if (bootgridState) {
            bootgrid.bootgridState = bootgridState;
        }

        bootgrids.push(bootgrid);
    }

    return bootgrids;
}

async function getBootgridState(pageState: PageState, menuItemName: string): Promise<BootgridState | null> {
    const selectedViewId = pageState?.selectedGridViews?.[menuItemName];

    if (!selectedViewId || selectedViewId === "default") {
        return null;
    }

    try {
        const view = await gridViewApi.getView(selectedViewId);
        return view?.BootgridState ?? null;
    } catch (error) {
        console.error("Failed to fetch grid view:", error);
        return null;
    }
}

function setupEventListeners(): void {
    addBottomTabsEventListener();
    addPostPageStateEventListeners();
}

function addPostPageStateEventListeners() {
    const viewSelects = document.querySelectorAll(".view-manager select");
    viewSelects.forEach((select) => {
        $(select).on("change", (i, e) => {
            postPageState();
        });
    });
}

function addBottomTabsEventListener() {
    if (document.getElementById("bottomTabs") !== null) {
        const bottomTabButtons = document.getElementById("bottomTabs").querySelectorAll("li");
        bottomTabButtons.forEach((button) => {
            button.addEventListener("click", (e) => {
                setTimeout(() => {
                    postPageState();
                }, 300);
            });
        });
    }
}

async function getPageState(): Promise<PageState> {
    // initialize with query param page state
    const url = new URL(window.location.href);
    const queryParams = new URLSearchParams(url.search);
    const page = queryParams.get("PageView");

    if (page) {
        const pageStateConstructor = new PageStateConstructor();
        return pageStateConstructor.constructPageStateFromPageView(page);
    }

    const bootgrid = document.querySelector("table.luscinia-bootgrid") as HTMLTableElement;

    if (!bootgrid) {
        return null;
    }

    // fetch user's page state
    const menuItemId = bootgrid.dataset.lusciniaMenuitemId;
    const userId = bootgrid.dataset.lusciniaUserid;
    if (menuItemId !== EmptyGuid.value) {
        const response = await fetch(`/page-state/${menuItemId}?userId=${userId}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return await response.json();
    }

    return null;
}